<script setup lang="ts">
import { useIntersectionAnimation } from "@/composables/use-intersection-animation";
import LocationAutocomplete from "@/components/marketing/jobs/LocationAutocomplete.vue";
import type { SelectItem, Option } from "@/types";

interface Props {
  searchQuery: string;
  locationQuery: string;
  selectedWorkingModel?: SelectItem;
  selectedExperience?: SelectItem;
  selectedCapacity?: SelectItem;
  workingModels: SelectItem[];
  experiences: SelectItem[];
  capacities: SelectItem[];
  filtersPending: boolean;
  hasActiveFilters: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:searchQuery": [value: string];
  "update:locationQuery": [value: string];
  "update:selectedWorkingModel": [value: SelectItem | undefined];
  "update:selectedExperience": [value: SelectItem | undefined];
  "update:selectedCapacity": [value: SelectItem | undefined];
  locationSelected: [location: any];
  locationCleared: [];
  clearFilters: [];
}>();

const { elementRef } = useIntersectionAnimation();

const localSearchQuery = computed({
  get: () => props.searchQuery,
  set: (value: string) => emit("update:searchQuery", value),
});

const localLocationQuery = computed({
  get: () => props.locationQuery,
  set: (value: string) => emit("update:locationQuery", value),
});

const localSelectedWorkingModel = computed({
  get: () => props.selectedWorkingModel,
  set: (value: SelectItem | undefined) => emit("update:selectedWorkingModel", value),
});

const localSelectedExperience = computed({
  get: () => props.selectedExperience,
  set: (value: SelectItem | undefined) => emit("update:selectedExperience", value),
});

const localSelectedCapacity = computed({
  get: () => props.selectedCapacity,
  set: (value: SelectItem | undefined) => emit("update:selectedCapacity", value),
});
</script>

<template>
  <section
    id="job-filters"
    ref="elementRef"
    class="relative py-8 lg:py-12 2xl:py-16 3xl:py-20 4xl:py-24 section-sand overflow-visible z-10"
  >
    <div
      class="max-w-[1400px] 2xl:max-w-[1600px] 3xl:max-w-[1800px] 4xl:max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 3xl:px-16 4xl:px-20"
    >
      <div class="fade-up">
        <div class="bg-white rounded-3xl 3xl:rounded-[2rem] shadow-warm-lg border border-sand/50">
          <!-- Filter Header -->
          <div
            class="bg-gradient-to-r from-primary/5 via-secondary/5 to-savanna/5 px-6 py-4 2xl:px-8 2xl:py-5 3xl:px-10 3xl:py-6 4xl:px-12 4xl:py-7 border-b border-sand/50 rounded-t-3xl"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3 2xl:gap-4">
                <div
                  class="w-10 h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14 4xl:w-16 4xl:h-16 rounded-xl 2xl:rounded-2xl bg-primary/10 flex items-center justify-center"
                >
                  <Icon name="material-symbols:filter-list" class="w-5 h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 text-primary" />
                </div>
                <div>
                  <h3 class="font-secondary font-semibold text-dark-gray text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl">
                    Find Your Perfect Job
                  </h3>
                  <p class="text-medium-gray text-sm 2xl:text-base 3xl:text-lg 4xl:text-xl">Filter by location, work type, and more</p>
                </div>
              </div>
              <button
                v-if="hasActiveFilters"
                @click="emit('clearFilters')"
                class="text-sm 2xl:text-base 3xl:text-lg 4xl:text-xl text-primary hover:text-primary-dark font-medium transition-colors flex items-center gap-2"
              >
                <Icon name="material-symbols:close" class="w-4 h-4 2xl:w-5 2xl:h-5" />
                Clear all
              </button>
            </div>
          </div>

          <!-- Filter Fields -->
          <div class="p-6 2xl:p-8 3xl:p-10 4xl:p-12">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 2xl:gap-6 3xl:gap-8">
              <!-- Search -->
              <div class="sm:col-span-2 lg:col-span-2 group">
                <FormInput
                  v-model="localSearchQuery"
                  name="search"
                  label="Search jobs"
                  placeholder="Job title, keywords..."
                  icon="material-symbols:search"
                  :disabled="filtersPending"
                />
              </div>

              <!-- Location -->
              <div class="sm:col-span-2 lg:col-span-2 group">
                <label class="px-1 text-xs 2xl:text-sm 3xl:text-base 4xl:text-lg group-focus-within:text-primary font-medium text-dark-gray"
                  >Location</label
                >
                <LocationAutocomplete
                  v-model="localLocationQuery"
                  placeholder="City, region, or country..."
                  :disabled="filtersPending"
                  class="[&_input]:rounded-xl [&_input]:border-sand"
                  @location-selected="(loc) => emit('locationSelected', loc)"
                  @location-cleared="emit('locationCleared')"
                />
              </div>

              <!-- Working Model -->
              <div>
                <SelectMenu
                  v-model="localSelectedWorkingModel"
                  name="workingModel"
                  label="Work Type"
                  placeholder="All Work Types"
                  :items="workingModels"
                  :allow-empty="true"
                  :disabled="filtersPending"
                />
              </div>

              <!-- Experience -->
              <div>
                <SelectMenu
                  v-model="localSelectedExperience"
                  name="experience"
                  label="Experience"
                  placeholder="All Levels"
                  :items="experiences"
                  :allow-empty="true"
                  :disabled="filtersPending"
                />
              </div>

              <!-- Employment Type -->
              <div>
                <SelectMenu
                  v-model="localSelectedCapacity"
                  name="capacity"
                  label="Employment"
                  placeholder="All Types"
                  :items="capacities"
                  :allow-empty="true"
                  :disabled="filtersPending"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Decorative blur -->
    <div class="absolute -top-24 -left-24 w-64 h-64 2xl:w-80 2xl:h-80 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
  </section>
</template>
